import { Box, Divider } from "@mui/material";
import { formatCurrencyNumber } from "@repo/legacy/components/helper/Index";
import { useTranslation } from "react-i18next";

import type { InvoiceItem } from "../types/CommercialDocumentTypes";

type TaxSummary = {
	code: string;
	rate: number;
	netAmount: number;
	taxAmount: number;
};

type InvoiceSummaryProps = {
	items: InvoiceItem[];
};

const calculateSummary = (items: InvoiceItem[]) => {
	const netTotal = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

	const taxByCode = items.reduce(
		(acc, item) => {
			const key = item.taxCode || "U0";
			if (!acc[key]) {
				acc[key] = { code: key, rate: item.tax || 0, netAmount: 0, taxAmount: 0 };
			}
			acc[key].netAmount += item.totalPrice || 0;
			acc[key].taxAmount += (item.totalPrice || 0) * (item.tax || 0);
			return acc;
		},
		{} as Record<string, TaxSummary>,
	);

	const taxDetails = Object.values(taxByCode)
		.filter((t) => t.netAmount > 0)
		.sort((a, b) => a.rate - b.rate);

	const taxTotal = taxDetails.reduce((sum, t) => sum + t.taxAmount, 0);
	const grossTotal = netTotal + taxTotal;

	return { netTotal, taxDetails, taxTotal, grossTotal };
};

const formatTaxLabel = (rate: number): string => {
	const percentage = Math.round(rate * 100);
	return `USt ${percentage}%`;
};

export const InvoiceSummary = ({ items }: InvoiceSummaryProps) => {
	const { t } = useTranslation();

	if (!items || items.length === 0) {
		return null;
	}

	const { netTotal, taxDetails, taxTotal, grossTotal } = calculateSummary(items);

	return (
		<Box
			sx={{
				marginTop: 4,
				marginBottom: 2,
				padding: 2,
				backgroundColor: "rgba(26, 69, 92, 0.1)",
				borderRadius: 2,
				maxWidth: 400,
				marginLeft: "auto",
			}}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					marginBottom: 1,
				}}>
				<span>{t("net-total")}</span>
				<strong>{formatCurrencyNumber(netTotal)}</strong>
			</Box>

			<Divider sx={{ marginY: 1 }} />

			{taxDetails.map((tax) => (
				<Box
					key={tax.code}
					sx={{
						display: "flex",
						justifyContent: "space-between",
						marginBottom: 0.5,
						fontSize: "0.9em",
						color: "text.secondary",
					}}>
					<span>
						{formatTaxLabel(tax.rate)} ({t("on")} {formatCurrencyNumber(tax.netAmount)})
					</span>
					<span>{formatCurrencyNumber(tax.taxAmount)}</span>
				</Box>
			))}

			{taxDetails.length > 0 && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						marginTop: 1,
						marginBottom: 1,
					}}>
					<span>{t("tax-total")}</span>
					<span>{formatCurrencyNumber(taxTotal)}</span>
				</Box>
			)}

			<Divider sx={{ marginY: 1 }} />

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					fontSize: "1.1em",
					fontWeight: "bold",
				}}>
				<span>{t("gross-total")}</span>
				<span>{formatCurrencyNumber(grossTotal)}</span>
			</Box>
		</Box>
	);
};

export default InvoiceSummary;
